import { fabric } from 'fabric';
import { Trans } from '@lingui/react/macro';
import { useEffect, useState } from 'react';
import alignment from 'modules/overlay-canvas/addons/alignment/alignment';
import { round } from 'shared/utils/round';
import LayerControls from './components/layer-controls';
import LockPosition from './components/lock-position';
import TransformSection from './shared/transform-section';
import { subscribeLiveTransform } from './shared/use-live-transform';
import { Item, LongDivider, ShortDivider } from './styles';
import { useEditorManager, useEditorActiveObjects } from 'modules/template-editor/ui/context';

function Image() {
  const activeObjects = useEditorActiveObjects();
  const fabricManager = useEditorManager();
  const object = activeObjects[0];

  const scale = object?.getObjectScaling();
  const { width, height, top, left } = object?.getBoundingRect(true) ?? defaultBounds;
  const lockMovementX = object?.lockMovementX;

  const [position, setPosition] = useState<{ x: number; y: number }>({ x: round(left), y: round(top) });
  const [size, setSize] = useState<{ width: number; height: number }>({ width: round(width), height: round(height) });
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);

  useEffect(() => {
    setPosition({ x: round(left), y: round(top) });
    setSize({ width: round(width), height: round(height) });
    setIsPositionLock(lockMovementX ?? false);

    let updatePosition = () => {
      const x = object?.get('left') ?? 0;
      const y = object?.get('top') ?? 0;
      setPosition({ x: round(x), y: round(y) });
    };

    let updateSize = () => {
      const { width, height } = object?.getBoundingRect(true) ?? defaultBounds;
      setSize({ width: round(width), height: round(height) });
    };

    const offPosition = subscribeLiveTransform(fabricManager?.canvas, updatePosition);
    const offSize = subscribeLiveTransform(fabricManager?.canvas, updateSize);

    return () => {
      offPosition();
      offSize();
    };

    // eslint-disable-next-line
  }, [activeObjects]);

  const onChangePosition = (x: number, y: number) => {
    object?.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
    setPosition({ x, y });
    fabricManager?.canvas.requestRenderAll();
  };

  const onChangeSize = (width: number, height: number) => {
    object?.set({ width: width / (scale?.scaleX ?? 1), height: height / (scale?.scaleY ?? 1) });
    setSize({ width, height });
    fabricManager?.canvas.requestRenderAll();
  };

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const showDebugInfo = false;
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <>
      <Item>
        <div>
          <Trans>Изображение</Trans>
        </div>
      </Item>
      <ShortDivider />
      <TransformSection
        position={position}
        size={size}
        onChangePosition={onChangePosition}
        onChangeSize={onChangeSize}
        onAlignCenterHorizontal={onClickAlignmentCenterHorizontal}
        onAlignCenterVertical={onClickAlignmentCenterVertical}
      />
      <LongDivider />
      <LayerControls object={object} />
      <LockPosition objects={[object]} onChange={setIsPositionLock} value={isPositionLock} />
      {isDev && showDebugInfo && (
        <>
          <LongDivider />
          <Item>
            <div>
              <Trans>Отладка</Trans>
            </div>
            <span>{JSON.stringify({ width: object.getScaledWidth(), height: object.getScaledHeight() }, null, 2)}</span>
            <span style={{ whiteSpace: 'pre' }}>{JSON.stringify(object?.toObject(), null, 2)}</span>
          </Item>
        </>
      )}
    </>
  );
}

export default Image;

const defaultBounds = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};
